/**
 * PATH: packages/pricelist/src/PricelistWorkbook.tsx
 * FILE_ID: PKG-PRICELIST-WORKBOOK-001
 * PURPOSE: The universal Excel-like pricelist editor: section tabs, an AG-Grid
 *          per section driven by that section's column_schema, add/computed
 *          columns, xlsx import/export, and draft (harvested) review — usable
 *          on the master SPA, the Quote Hub and SAM/Newmark.
 * WHY: One pricelist surface for every tenant; SAM steel columns and a custom
 *      org's own columns are both "just columns" here.
 * FLOW: client → usePricelistWorkbook (load/edit/save) → tabs + LazyWorkbookGrid
 * RELATED: usePricelistWorkbook, columns, xlsx, ColumnDialog, LazyWorkbookGrid
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Loader2, FolderPlus, Filter, Search, X, Eye, EyeOff, Pencil, PencilOff, Percent, SlidersHorizontal } from 'lucide-react'
import { useIsPhone } from '@quotech/ui'
import type { ColumnDef, PickedItem, PricelistClient } from './types'
import { usePricelistWorkbook } from './usePricelistWorkbook'
import { distinctSuppliers, filterSectionsBySupplier } from './supplierFilter'
import {
  filterRowsByBulkPricing,
  firstSectionIdWithBulkPricing,
  type BulkPricingMode,
} from './bulkPricingFilter'
import { availableFormulaFields } from './formulaFields'
import { buildColumnDefs, buildAllSectionsColumnDefs } from './columns'
import { resolvePickedItem } from './pickedItem'
import { WORKBOOK_STYLES } from './workbookStyles'
import { downloadWorkbook } from './xlsx'
import LazyWorkbookGrid from './LazyWorkbookGrid'
import PricelistHeaderMenus from './PricelistHeaderMenus'
import PricelistRowMenus from './PricelistRowMenus'
import PricelistMobileTips from './PricelistMobileTips'
import SupplierRibbon from './SupplierRibbon'
import DiscountDialog from './DiscountDialog'
import ColumnDialog from './ColumnDialog'
import ColumnManagerDialog from './ColumnManagerDialog'
import PricelistToolbar from './PricelistToolbar'
import SectionTemplatePicker from './SectionTemplatePicker'
import { buildColumnViewPayload, filterArrangerColumns } from './iqSchema'

export interface PricelistWorkbookProps {
  client: PricelistClient
  pricebookId: string
  /** Read-only mode (e.g. client-facing). Defaults to editable. */
  editable?: boolean
  /** Title shown above the tab strip. */
  title?: string
  /**
   * When set, each grid row shows a "→ add to quote" action. Used by the Quote
   * Builder split-screen drawer to pick a pricelist row into the open quote.
   */
  onPickItem?: (picked: PickedItem) => void
  /**
   * false = the pricelist shows supplier COST data only (Markup % / Sell core
   * columns removed — those live in the quote builder). Default true keeps the
   * legacy layout for hosts that still want it.
   */
  pricingColumns?: boolean
  /** Shared per-user Pricelist IQ state. Defaults off; hosts pass the live preference. */
  iqEnabled?: boolean
  /** Owner/admin-controlled organisation schema arranger. */
  schemaArrangerOpen?: boolean
  onSchemaArrangerClose?: () => void
  /**
   * true = the workbook opens READ-ONLY and an authorised user must click
   * "Edit" to make changes; Save then asks for confirmation (2026-07-14
   * task 9). Default false keeps legacy always-editable hosts unchanged.
   */
  requireEditMode?: boolean
}

export default function PricelistWorkbook({
  client,
  pricebookId,
  editable = true,
  title = 'Pricelist',
  onPickItem,
  pricingColumns = true,
  iqEnabled = false,
  schemaArrangerOpen = false,
  onSchemaArrangerClose,
  requireEditMode = false,
}: PricelistWorkbookProps) {
  const wb = usePricelistWorkbook({ client, pricebookId, editable })
  const isPhone = useIsPhone()
  // Task 9: view mode by default; an explicit "Edit" click enables editing.
  const [editMode, setEditMode] = useState(!requireEditMode)
  const activeEditable = wb.activeEditable && editMode
  const [colDialog, setColDialog] = useState(false)
  const [colMgr, setColMgr] = useState(false)
  const [colMgrSaving, setColMgrSaving] = useState(false)
  const [discountDialog, setDiscountDialog] = useState(false)
  const [discountSaving, setDiscountSaving] = useState(false)
  const [sectionDialog, setSectionDialog] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [supplierFilter, setSupplierFilter] = useState('')
  const [bulkFilter, setBulkFilter] = useState<BulkPricingMode>('')
  const [rowSearch, setRowSearch] = useState('')
  // Columns flagged hidden_by_default stay off unless the viewer opts in.
  const [showHiddenCols, setShowHiddenCols] = useState(false)
  // Right-click row menu: viewport coords + the row it was opened on.
  const [rowMenu, setRowMenu] = useState<{ rowId: string; x: number; y: number } | null>(null)
  // Right-click column-header menu (Excel-style "Hide column").
  const [headerMenu, setHeaderMenu] = useState<{ colKey: string; x: number; y: number } | null>(null)
  const [hidingColumn, setHidingColumn] = useState(false)

  // Post-harvest each "Harvested — <Supplier>" section carries a real Supplier
  // FK (surfaced as supplier_name). These drive the toolbar Supplier filter,
  // which narrows the visible section tabs to one supplier.
  const supplierOptions = useMemo(() => distinctSuppliers(wb.sections), [wb.sections])
  const visibleSections = useMemo(
    () => filterSectionsBySupplier(wb.sections, supplierFilter),
    [wb.sections, supplierFilter],
  )
  const onSupplierFilter = (value: string) => {
    setSupplierFilter(value)
    const vis = filterSectionsBySupplier(wb.sections, value)
    if (vis.length && !vis.some((s) => s.id === wb.activeId)) wb.setActiveId(vis[0].id)
  }

  // Bulk pricing is row-level, not supplier-tab-level. When the user has not
  // narrowed Supplier, preload visible tabs and move to the first tab with a
  // matching bulk row so "All suppliers + Bulk pricing" never looks empty just
  // because the current section has no bulk SKUs.
  useEffect(() => {
    if (!bulkFilter || supplierFilter || !visibleSections.length) return
    void wb.preloadSections(visibleSections.map((s) => s.id))
  }, [bulkFilter, supplierFilter, visibleSections, wb.preloadSections])

  useEffect(() => {
    if (!bulkFilter || supplierFilter || !visibleSections.length) return
    if (filterRowsByBulkPricing(wb.rows, bulkFilter).length > 0) return
    const nextId = firstSectionIdWithBulkPricing(visibleSections, wb.rowsBySection, bulkFilter)
    if (nextId && nextId !== wb.activeId) wb.setActiveId(nextId)
  }, [bulkFilter, supplierFilter, visibleSections, wb.activeId, wb.rows, wb.rowsBySection, wb.setActiveId])

  // The org schema is uniform across sections (columns/apply_view). The first
  // real section's schema therefore stands in for the whole book — it drives
  // the "All sections" overview so the overview shows the same columns in the
  // same order as every section tab.
  const uniformSchema = useMemo(
    () => wb.sections.find((s) => !s.aggregate && !s.synthetic)?.column_schema || [],
    [wb.sections],
  )

  const columnDefs = useMemo(() => {
    if (!wb.activeSection) return []
    // Read-only "All sections" overview: uniform org schema + a Section column.
    if (wb.activeSection.aggregate)
      return buildAllSectionsColumnDefs(iqEnabled, uniformSchema, {
        pricingColumns,
        showHidden: showHiddenCols,
      })
    return buildColumnDefs(wb.activeSection.column_schema || [], {
      editable: activeEditable,
      pricingColumns,
      showHidden: showHiddenCols,
      iqEnabled,
    })
  }, [wb.activeSection, activeEditable, pricingColumns, showHiddenCols, iqEnabled, uniformSchema])

  const hiddenColCount = useMemo(() => {
    const schema = wb.activeSection?.aggregate
      ? uniformSchema
      : wb.activeSection?.column_schema || []
    return schema.filter((c) => c.hidden_by_default === true).length
  }, [wb.activeSection, uniformSchema])

  // Row-level filter (orthogonal to the section-level supplier filter): narrow
  // the active section's rows by their per-SKU bulk_pricing flag. Edits/save run
  // on wb.rows (the full hook state), so filtering the view never loses data.
  const visibleRows = useMemo(
    () => filterRowsByBulkPricing(wb.rows, bulkFilter),
    [wb.rows, bulkFilter],
  )
  const searchedRows = useMemo(() => {
    const query = rowSearch.trim().toLowerCase()
    if (query.length < 2) return visibleRows
    return visibleRows.filter((row) => {
      // GridRow is FLAT (itemToRow spreads item.data onto the row), so the old
      // `row.data` spread searched nothing. Search the requested identity
      // fields: SKU, description, Section/Product type (+ product_details,
      // short_name, code) — 2026-07-08 search-area expansion.
      const haystack = [
        row.item_code,
        row.item_name,
        row.description,
        row.unit,
        row.sku,
        row.section_product_type,
        row.product_details,
        row.short_name,
        row.product_code,
        row.system,
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(query)
    })
  }, [visibleRows, rowSearch])

  // Fields a new column's formula can reference (core + this section's columns).
  const formulaFields = useMemo(
    () => availableFormulaFields(wb.activeSection?.column_schema || []),
    [wb.activeSection],
  )
  const arrangerColumns = useMemo(() => {
    const schema = wb.activeSection?.aggregate
      ? wb.sections.find((section) => !section.aggregate)?.column_schema || []
      : wb.activeSection?.column_schema || []
    return filterArrangerColumns(schema, iqEnabled)
  }, [wb.activeSection, wb.sections, iqEnabled])

  const syntheticView = wb.isSyntheticActive

  const draftCount = useMemo(
    () => wb.rows.filter((r) => r.is_active === false).length,
    [wb.rows],
  )

  const onAddColumn = async (column: ColumnDef) => {
    if (!wb.activeSection) return
    setColDialog(false)
    try {
      await wb.runColumnOp(client.addColumn(pricebookId, wb.activeSection.id, column))
    } catch {
      /* error surfaced via wb.error */
    }
  }

  const onSaveColumns = async (next: ColumnDef[], opts?: { applyToAllSections?: boolean }) => {
    if (!wb.activeSection) return
    setColMgrSaving(true)
    try {
      if (schemaArrangerOpen && client.applyColumnView) {
        await client.applyColumnView(pricebookId, buildColumnViewPayload(next))
        await wb.reload()
        onSchemaArrangerClose?.()
        return
      }
      // Uniform org schema: the DEFAULT save pushes this order + hidden set to
      // EVERY section (owner/admin only — the server enforces the role). The
      // per-section update still runs first on a real section so label/type/
      // formula edits (not carried by apply_view) are persisted.
      if (!wb.activeSection.aggregate) {
        await wb.runColumnOp(
          client.updateSection(pricebookId, wb.activeSection.id, { column_schema: next }),
        )
      }
      if (opts?.applyToAllSections && client.applyColumnView) {
        // buildColumnViewPayload carries order + hidden + per-column align.
        await client.applyColumnView(pricebookId, buildColumnViewPayload(next))
        await wb.reload()
      }
      setColMgr(false)
    } catch {
      /* error surfaced via wb.error */
    } finally {
      setColMgrSaving(false)
    }
  }

  // Task 3: fixed supplier discount — the endpoint recomputes discounted_cost
  // server-side (snapshot first) and stamps supplier + item last-update dates.
  const onApplyDiscount = async (discount: number, itemIds: string[] | undefined) => {
    if (!wb.activeSection || !client.applyDiscount) return
    setDiscountSaving(true)
    try {
      const sectionId = wb.activeSection.id
      await client.applyDiscount(pricebookId, sectionId, {
        discount,
        ...(itemIds ? { item_ids: itemIds } : {}),
      })
      // Refresh section metadata (ribbon default discount) AND the rows
      // (discounted_cost / last_price_update now live server-side).
      await Promise.all([wb.reload(), wb.reloadSection(sectionId)])
      setDiscountDialog(false)
    } catch {
      /* error surfaced via wb.error on reload; dialog stays open */
    } finally {
      setDiscountSaving(false)
    }
  }

  // Excel-style header right-click → Hide: flags the column hidden_by_default
  // and persists the view org-wide (all sections) via columns_apply_view.
  // Structure-only — item data and quote costing are untouched, and the column
  // stays recoverable from the Hidden tab / "Show hidden columns".
  const onHideColumn = async (key: string) => {
    if (!client.applyColumnView) return
    const schema = wb.activeSection?.aggregate
      ? uniformSchema
      : wb.activeSection?.column_schema || []
    if (!schema.some((c) => c.key === key)) return
    setHidingColumn(true)
    try {
      const next = schema.map((c) => (c.key === key ? { ...c, hidden_by_default: true } : c))
      await client.applyColumnView(pricebookId, buildColumnViewPayload(next))
      await wb.reload()
    } catch {
      /* error surfaced via wb.error on reload */
    } finally {
      setHidingColumn(false)
    }
  }

  const onAddSection = async (name: string, columns: ColumnDef[]) => {
    setSectionDialog(false)
    try {
      await wb.addSection(name, columns)
    } catch {
      /* error surfaced via wb.error */
    }
  }

  const onExport = () => {
    if (!wb.activeSection) return
    downloadWorkbook(
      [{ name: wb.activeSection.name, schema: wb.activeSection.column_schema || [], rows: wb.rows }],
      `${title}-${wb.activeSection.name}`,
    )
  }

  const onImportPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !wb.activeSection) return
    const buf = await file.arrayBuffer()
    await wb.importFile(wb.activeSection.id, buf)
    if (fileRef.current) fileRef.current.value = ''
  }

  if (wb.loading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pricelist…
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
          {supplierOptions.length > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Filter className="h-3.5 w-3.5" />
              <select
                value={supplierFilter}
                onChange={(e) => onSupplierFilter(e.target.value)}
                className="qb-pl-select"
                aria-label="Filter sections by supplier"
              >
                <option value="">All suppliers</option>
                {supplierOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!!wb.activeSection && (
            <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Filter className="h-3.5 w-3.5" />
              <select
                value={bulkFilter}
                onChange={(e) => setBulkFilter(e.target.value as BulkPricingMode)}
                className="qb-pl-select"
                aria-label="Filter rows by bulk pricing"
              >
                <option value="">All pricing</option>
                <option value="yes">Bulk pricing only</option>
                <option value="no">No bulk pricing</option>
              </select>
            </label>
          )}
          {!!wb.activeSection && (
            <label className="flex min-w-[220px] items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Search className="h-3.5 w-3.5" />
              <span className="sr-only">Search pricelist rows</span>
              <span className="relative flex-1">
                <input
                  type="search"
                  value={rowSearch}
                  onChange={(e) => setRowSearch(e.target.value)}
                  placeholder="Search code, name, description..."
                  className="h-8 w-full rounded border border-slate-300 bg-white px-2 pr-7 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-500 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                  aria-label="Search pricelist rows"
                />
                {rowSearch && (
                  <button
                    type="button"
                    aria-label="Clear pricelist search"
                    onClick={() => setRowSearch('')}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </label>
          )}
          {hiddenColCount > 0 && (
            <button
              type="button"
              onClick={() => setShowHiddenCols((v) => !v)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              title="Columns hidden by the org default view"
            >
              {showHiddenCols ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showHiddenCols ? 'Hide hidden columns' : `Show hidden columns (${hiddenColCount})`}
            </button>
          )}
          {/* Column visibility is viewable by everyone. Non-editors get a
              read-only "Columns" dialog (see every column incl. hidden);
              editors get the full manager via the toolbar below. */}
          {!wb.editable && !!wb.activeSection && (
            <button
              type="button"
              onClick={() => setColMgr(true)}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
              title="View the pricelist columns (visible and hidden)"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Columns
            </button>
          )}
        </div>
        {wb.editable && (
          <div className="flex flex-wrap items-center gap-2">
            {requireEditMode && (
              <button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                className={editMode ? 'qb-pl-btn-primary' : 'qb-pl-btn'}
                title={editMode
                  ? 'Return to read-only view (unsaved edits stay in the grid until you Save or reload)'
                  : 'Enter edit mode to change price-master data'}
              >
                {editMode ? <PencilOff className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                {editMode ? 'Editing' : 'Edit'}
              </button>
            )}
            {activeEditable && !wb.isAggregateActive && !syntheticView && !!client.applyDiscount && (
              <button
                type="button"
                onClick={() => setDiscountDialog(true)}
                className="qb-pl-btn"
                title="Apply a fixed supplier discount to this section's products (net cost = list price × (1 − discount%))"
              >
                <Percent className="h-4 w-4" /> Discount
              </button>
            )}
            <PricelistToolbar
              syntheticView={syntheticView}
              canAddColumn={wb.canAddColumn && editMode}
              canManageColumns={
                wb.canAddColumn ||
                (wb.isAggregateActive && uniformSchema.length > 0 && !!client.applyColumnView)
              }
              activeEditable={activeEditable}
              hasActiveSection={!!wb.activeSection}
              saving={wb.saving}
              onNewSection={() => setSectionDialog(true)}
              onAddColumn={() => setColDialog(true)}
              onManageColumns={() => setColMgr(true)}
              onImport={() => fileRef.current?.click()}
              onExport={onExport}
              onAddRow={() => wb.activeSection && wb.addRow(wb.activeSection.id)}
              onSave={() => {
                if (!wb.activeSection) return
                // Task 9: an explicit confirmation guards every price-master save.
                if (requireEditMode && !window.confirm('Save these changes to the price master?')) return
                wb.saveSection(wb.activeSection.id)
              }}
            />
          </div>
        )}
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onImportPick} />
      </div>

      {wb.error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          {wb.error}
        </div>
      )}

      {syntheticView && (
        <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
          <b>{wb.activeSection?.item_count ?? wb.rows.length}</b> item
          {(wb.activeSection?.item_count ?? wb.rows.length) === 1 ? '' : 's'} in this pricelist aren't grouped
          into a section yet. Edit cells freely and click <b>Save</b> to organise them into the editable
          “{wb.activeSection?.name}” tab — or use <b>New section</b> to add trade tabs (Steel, Waterproofing…).
        </div>
      )}

      {draftCount > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          {draftCount} draft row{draftCount === 1 ? '' : 's'} harvested from quotes — review, edit, then
          tick <b>is_active</b> and Save to publish.
        </div>
      )}

      {wb.activeSection && !wb.activeSection.aggregate && !wb.activeSection.synthetic && (
        // Task 7: supplier ribbon — keyed by section so a tab switch swaps the
        // whole panel (no stale supplier info).
        <SupplierRibbon key={wb.activeSection.id} section={wb.activeSection} rows={wb.rows} />
      )}

      {isPhone && activeEditable && <PricelistMobileTips />}

      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700">
        {visibleSections.map((s) => (
          <button
            key={s.id}
            onClick={() => wb.setActiveId(s.id)}
            className={
              'relative -mb-px rounded-t-md px-3 py-1.5 text-sm ' +
              (s.id === wb.activeId
                ? 'border border-b-white bg-white font-medium text-slate-900 dark:border-slate-700 dark:border-b-slate-900 dark:bg-slate-900 dark:text-white'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200')
            }
          >
            {s.name}
            {typeof s.item_count === 'number' && (
              <span className="ml-1.5 text-[11px] text-slate-400">{s.item_count}</span>
            )}
          </button>
        ))}
        {wb.editable && (
          <button
            onClick={() => setSectionDialog(true)}
            title="Add a section (Steel, Waterproofing, …) or a blank one"
            className="relative -mb-px flex items-center gap-1 rounded-t-md px-3 py-1.5 text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
          >
            <Plus className="h-3.5 w-3.5" /> New section
          </button>
        )}
        {!wb.sections.length && (
          <span className="px-2 py-2 text-sm text-slate-400">No sections yet.</span>
        )}
      </div>

      <div className="min-h-[420px] flex-1">
        {wb.activeSection && wb.rowsLoading ? (
          // Rows for this tab are still in flight — show a real loading state
          // instead of AG-Grid's misleading "No rows to show" (task 12).
          <div className="flex h-64 items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading rows…
          </div>
        ) : wb.activeSection ? (
          <LazyWorkbookGrid
            rows={searchedRows}
            columnDefs={columnDefs}
            editable={activeEditable}
            onCellEdit={(rowId, field, value) =>
              wb.editCell(wb.activeSection!.id, rowId, field, value)
            }
            onDeleteRow={(rowId) => wb.removeRow(wb.activeSection!.id, rowId)}
            onRowContextMenu={
              activeEditable
                ? ({ rowId, clientX, clientY }) =>
                    setRowMenu({ rowId, x: clientX, y: clientY })
                : undefined
            }
            onPickItem={
              onPickItem
                ? (row) => onPickItem(resolvePickedItem(row, wb.activeSection?.column_schema || []))
                : undefined
            }
            onHeaderContextMenu={
              activeEditable && !!client.applyColumnView
                ? ({ colKey, clientX, clientY }) => {
                    const schema = wb.activeSection?.aggregate
                      ? uniformSchema
                      : wb.activeSection?.column_schema || []
                    // Only columns carried by the schema can be hidden (core
                    // grid-only fields and action columns are not persistable).
                    if (!schema.some((c) => c.key === colKey && c.hidden_by_default !== true)) return
                    setHeaderMenu({ colKey, x: clientX, y: clientY })
                  }
                : undefined
            }
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-slate-400">
            <p>No sections yet — start with an industry template or a blank table.</p>
            {wb.editable && (
              <button onClick={() => setSectionDialog(true)} className="qb-pl-btn-primary">
                <FolderPlus className="h-4 w-4" /> New section
              </button>
            )}
          </div>
        )}
      </div>

      <style>{WORKBOOK_STYLES}</style>

      <ColumnDialog
        open={colDialog}
        onClose={() => setColDialog(false)}
        onSubmit={onAddColumn}
        availableFields={formulaFields}
      />
      <ColumnManagerDialog
        open={colMgr || schemaArrangerOpen}
        columns={schemaArrangerOpen
          ? arrangerColumns
          : (wb.activeSection?.aggregate
              ? uniformSchema
              : wb.activeSection?.column_schema || [])}
        availableFields={schemaArrangerOpen ? [] : formulaFields}
        saving={colMgrSaving}
        canApplyToAllSections={!schemaArrangerOpen && !!client.applyColumnView}
        arrangeOnly={schemaArrangerOpen || wb.isAggregateActive}
        viewOnly={!schemaArrangerOpen && !wb.editable}
        onClose={() => schemaArrangerOpen ? onSchemaArrangerClose?.() : setColMgr(false)}
        onSave={onSaveColumns}
      />
      <SectionTemplatePicker
        open={sectionDialog}
        onClose={() => setSectionDialog(false)}
        onSubmit={onAddSection}
      />
      <DiscountDialog
        open={discountDialog}
        sectionName={wb.activeSection?.name ?? ''}
        rows={wb.rows}
        saving={discountSaving}
        onClose={() => setDiscountDialog(false)}
        onApply={onApplyDiscount}
      />

      <PricelistHeaderMenus
        isPhone={isPhone}
        target={headerMenu}
        hiding={hidingColumn}
        onHide={(colKey) => void onHideColumn(colKey)}
        onManage={() => setColMgr(true)}
        onClose={() => setHeaderMenu(null)}
      />

      <PricelistRowMenus
        isPhone={isPhone}
        target={rowMenu && wb.activeSection ? rowMenu : null}
        onInsertAbove={(rowId) => {
          const idx = wb.rows.findIndex((r) => r.id === rowId)
          wb.insertRowAt(wb.activeSection!.id, idx === -1 ? wb.rows.length : idx)
        }}
        onInsertBelow={(rowId) => {
          const idx = wb.rows.findIndex((r) => r.id === rowId)
          wb.insertRowAt(wb.activeSection!.id, idx === -1 ? wb.rows.length : idx + 1)
        }}
        onDuplicate={(rowId) => wb.duplicateRow(wb.activeSection!.id, rowId)}
        onDelete={(rowId) => wb.removeRow(wb.activeSection!.id, rowId)}
        onClose={() => setRowMenu(null)}
      />
    </div>
  )
}
